import React from "react"
import { Slot } from "@radix-ui/react-slot"

import { getSyncDictionary } from "@/lib/sync-dictionary"
import { cn, ensureHttpUrl } from "@/lib/utils"
import { Link, RemixIcon, Tag, Typography } from "@/components/ui/atoms"

interface ICardAnnuaireProps {
  id?: string
  asChild?: string
  className?: string
  title: string
  description: string
  location: string
  number: string
  tagLabel: string
  address: string
  website: string
  googleMapUrl: string
}

const CardAnnuaire = React.forwardRef<HTMLDivElement, ICardAnnuaireProps>(
  (
    {
      asChild = false,
      className,
      title,
      description,
      location,
      number,
      address,
      website,
      googleMapUrl,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"

    const hasAddress = Boolean(address?.trim())
    const hasNumber = Boolean(number?.trim())
    const hasWebsite = Boolean(website?.trim())
    const formattedWebsite = ensureHttpUrl(website)

    return (
      <Component
        className={cn(
          "mx-auto max-w-[1000px] rounded-100 bg-neutral-100 p-6 md:p-8",
          className
        )}
        ref={ref}
        {...props}
      >
        <div className="justify-between md:flex">
          <div className="mb-5 md:mb-0">
            <Typography as="h2" className="pb-3" variant="headingM">
              {title}
            </Typography>
            <Typography
              as="h3"
              className="text-neutral-400"
              variant="headingXS"
            >
              {description}
            </Typography>
          </div>
          <div>
            <Tag variant="light" icon="Pin" label={location} />
          </div>
        </div>
        <div className="mt-8 flex flex-col justify-between md:flex-row md:items-end">
          <div>
            {hasAddress ? (
              <div className="mb-6 flex">
                <RemixIcon
                  fill="#736F69"
                  className="mr-5 shrink-0"
                  size="md"
                  name="Pin"
                />
                <span>{address}</span>
              </div>
            ) : null}

            {hasNumber ? (
              <div className="mb-6 flex">
                <RemixIcon
                  fill="#736F69"
                  className="mr-5"
                  size="md"
                  name="Phone"
                />

                <Link
                  chapters={[title, number]}
                  variant="ghost"
                  className="p-0 font-400 no-underline"
                  href={"tel:" + number.trim()}
                >
                  {number}
                </Link>
              </div>
            ) : null}

            {hasWebsite ? (
              <div className="flex">
                <RemixIcon
                  fill="#736F69"
                  className="mr-5"
                  size="md"
                  name="Earth"
                />
                <Link
                  chapters={[title, website]}
                  variant="ghost"
                  className="p-0 font-400"
                  href={formattedWebsite}
                  target="_blank"
                >
                  {website}
                </Link>
              </div>
            ) : null}
          </div>
          <div className="mt-8 self-end md:mt-0">
            <Link
              chapters={[title, getSyncDictionary().page.annuaire.googleMaps]}
              variant="ghost"
              iconRight="ExternalLinkLine"
              className="p-0"
              target="_blank"
              href={googleMapUrl}
            >
              {getSyncDictionary().page.annuaire.googleMaps}
            </Link>
          </div>
        </div>
      </Component>
    )
  }
)

export { CardAnnuaire, type ICardAnnuaireProps }
